import { useMemo, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { Col, Row, Card, CardBody, Button } from "reactstrap"

import DeleteModal from "components/Common/DeleteModal"
import TableContainer from "components/Common/TableContainer"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import ButtonTooltip from "components/Common/ButtonTooltip"
import ImageViewer from "components/Common/ImageViewer/ImageViewer"
import FullscreenWrapper from "components/Common/FullscreenWrapper"
import ReadyStockFilterButtons from "components/Common/ReadyStockFilterButtons"

import { useDeleteVehicleStock } from "hooks/stockHook"
import { useVehicleImporterStocks } from "hooks/useVehicleImporter"
import { useListParams } from "hooks/useListParams"
import { useResponsiveCollapse } from "hooks/useWindowSize"
import { downloadVehiclePdf, downloadVehicleStockPdf } from "helpers/pdf_helper"
import { getStockColumns } from "components/Common/stock-table/stockColumns"
import VehicleFilterDropdown from "./components/VehicleFilterDropdown"

const ImporterStocks = ({ importerId }) => {
  document.title = "Importer Stocks"

  const navigate = useNavigate()

  // ---------------- STATE ----------------
  const [stock, setStock] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [selectedImageUrl, setSelectedImageUrl] = useState("")
  const [imageTitle, setImageTitle] = useState("Auction Sheet")

  const openImageViewer = url => {
    setViewerOpen(true)
    setSelectedImageUrl(url || "")
  }

  // ---------------- LIST PARAMS ----------------
  const initParams = {
    page: 1,
    per_page: 10,
    search: "",
    grouped: true,
  }
  const { params, setPage, updateParams, resetParams, hasActiveFilters } =
    useListParams(initParams)

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: params.per_page,
      ...params,
    }),
    [params]
  )

  // ---------------- API ----------------
  const {
    data: stocksResponse,
    isLoading,
    refetch,
    isRefetching,
  } = useVehicleImporterStocks(
    { importerId, ...queryParams },
    Boolean(importerId)
  )

  const { mutateAsync: deleteStock, isPending: deleteLoading } =
    useDeleteVehicleStock()

  // ---------------- DATA ----------------
  const groupedData = stocksResponse?.data?.grouped || []
  const data = stocksResponse?.data?.vehicles?.data || []
  const paginationInfo = stocksResponse?.data?.vehicles
  const totalStocks = paginationInfo?.total ?? 0

  const handleClearFilters = () => {
    resetParams([], initParams)
  }

  // In your parent component
  const handleVehicleSelect = params => {
    if (Object.keys(params || {}).length > 0) {
      updateParams(prev => ({
        ...prev,
        ...params,
        page: 1, // reset page
      }))
    } else {
      // Clear filters
      handleClearFilters()
    }
  }

  const handleReadyStockFilterChange = value => {
    updateParams(prev => ({
      ...prev,
      vs_ready_stock: value,
      page: 1,
    }))
  }

  // ---------------- HANDLERS ----------------
  const handleAddNewClick = () => {
    navigate(`/stock-add-edit?importerId=${importerId}`)
  }

  const handleStockClick = useCallback(stockData => {
    if (stockData?.stock_id) {
      navigate(`/stock-add-edit/${stockData.stock_id}?importerId=${importerId}`)
    }
  }, [])

  const handleDeleteClick = useCallback(stockData => {
    setStock(stockData)
    setDeleteModal(true)
  }, [])

  const handleDelete = async () => {
    if (!stock?.id) return

    try {
      const res = await deleteStock(stock.id)
      toast.success(res?.message ?? "Stock deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      toast.error("Failed to delete stock.")
    }
  }

  const handleDownloadSingle = async stockId => {
    try {
      await downloadVehiclePdf(stockId)
    } catch {}
  }

  const handleDownloadAll = async () => {
    try {
      await downloadVehicleStockPdf(queryParams)
    } catch {}
  }

  const { isMobile, toggleRow, isExpanded } = useResponsiveCollapse()

  const columns = useMemo(
    () =>
      getStockColumns({
        navigate,
        isMobile,
        toggleRow,
        isExpanded,
        handleStockClick,
        onClickDelete: handleDeleteClick,
        onClickDownload: handleDownloadSingle,
        openImageViewer,
        setImageTitle,
      }),
    [navigate, isMobile, toggleRow, isExpanded]
  )

  // ---------------- RENDER ----------------
  return (
    <FullscreenWrapper>
      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <ImageViewer
        images={[selectedImageUrl]}
        initialIndex={0}
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title={imageTitle}
      />

      <Row>
        <Col lg="12">
          <Card>
            {/* HEADER */}
            <CardBody className="border-bottom">
              <Row className="justify-content-between align-items-center">
                <Col xs={12} sm={6}>
                  <h5 className="mb-0">
                    Importer Stocks{" "}
                    <span className="badge badge-soft-info">{totalStocks}</span>
                  </h5>
                </Col>

                <Col xs={12} sm={6}>
                  <div className="text-sm-end mt-2 mt-sm-0 me-5">
                    <Button
                      color="primary"
                      onClick={handleAddNewClick}
                      className="me-1"
                    >
                      Add New Stock
                    </Button>

                    <ButtonTooltip
                      type="refresh"
                      btnId="refreshStockList"
                      onClick={refetch}
                      loading={isRefetching}
                      color="light"
                      className="btn btn-light me-1"
                    />

                    {hasActiveFilters(["grouped"]) && (
                      <Button
                        className="ms-1"
                        color="danger"
                        onClick={handleClearFilters}
                      >
                        Reset Filters
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>

            {/* BODY */}
            <CardBody>
              <Row className="mb-3">
                <Col>
                  <div className="d-flex flex-column flex-lg-row gap-2 justify-content-lg-between">
                    <GlobalInputSearch
                      value={params.search}
                      onChange={val =>
                        updateParams(prev => ({
                          ...prev,
                          search: val,
                          page: 1,
                        }))
                      }
                    />
                    <div className="d-flex flex-column flex-sm-row gap-2 align-items-sm-start">
                      <ReadyStockFilterButtons
                        value={params.vs_ready_stock || ""}
                        onChange={handleReadyStockFilterChange}
                      />
                      <VehicleFilterDropdown
                        data={groupedData}
                        label="Filter Vehicles"
                        onSelect={handleVehicleSelect}
                        multiSelect={true}
                        activeFilters={params}
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              <TableContainer
                columns={columns}
                data={data}
                isPagination
                paginationInfo={paginationInfo}
                onPageChange={setPage}
                loading={isLoading || isRefetching}
                className="table-responsive"
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </FullscreenWrapper>
  )
}

export default ImporterStocks
